// Plans: the three standard Magically Linked tiers, what each one unlocks, and how an account's
// effective entitlements are worked out. Pure (no I/O) so the API, the portal and the extension
// always agree. Rationale and the reseller workflow: docs/PLANS.md.
//
//   Starter $9/mo   Know your audience: CRM inside LinkedIn, Follower Tracker, Scheduler, Auto-Caption.
//   Growth  $49/mo  Your funnel on autopilot: + Campaigns, AI DM replies, AI comment replies, Auto-Tag,
//                   Profile Snapshots. 2 LinkedIn accounts, 3 live campaigns per account.
//   Pro     $99/mo  Borrow big audiences: + Dream 100, Spotlight, API & MCP. 5 LinkedIn accounts,
//                   unlimited campaigns.
//
// Some things are in every plan and can't be switched off (CORE_INCLUDED): the CRM, the LinkedIn
// overlay, inbox sync, saved replies, analytics, Engagement Buddies (it also runs the welcome
// follows) and support. Other features depend on them.
//
// Account safety is never for sale: every plan gets the same daily limits, pacing, active hours
// and block protection. Plans differ in what Magically Linked does, not in how hard it pushes LinkedIn.
//
// How an account's features are worked out (resolveEntitlements):
//   1. Admins and resellers always have everything (they run the product).
//   2. The base tier is the reseller package's tier, else the account's plan (trial, pro, lifetime
//      and agency all mean Pro, so nobody who had everything loses anything).
//   3. A reseller package can switch features on or off on top of its tier.
//   4. Per-account switches (set by the reseller or an admin) apply last.
// Limits come from the base tier. A feature switched on outside its tier uses the limits of the
// lowest tier that includes it (Starter + Campaigns gets Growth's 3 live campaigns).

import type { Plan, Role } from "./types";

export type PlanTier = "starter" | "growth" | "pro";
export const PLAN_TIERS = ["starter", "growth", "pro"] as const satisfies readonly PlanTier[];

/** Features a plan unlocks. Everything else is in every plan (CORE_INCLUDED). */
export type PlanFeature =
  | "followers"
  | "scheduler"
  | "autocaption"
  | "outreach"
  | "autoresponder"
  | "comments"
  | "autotag"
  | "snapshots"
  | "spotlight"
  | "dream100"
  | "integrations";

export const PLAN_FEATURES = [
  "followers",
  "scheduler",
  "autocaption",
  "outreach",
  "autoresponder",
  "comments",
  "autotag",
  "snapshots",
  "spotlight",
  "dream100",
  "integrations",
] as const satisfies readonly PlanFeature[];

export interface PlanLimits {
  /** Live (active) campaigns per LinkedIn account. null = unlimited, 0 = no campaigns. */
  liveCampaigns: number | null;
}

export interface PlanFeatureInfo {
  key: PlanFeature;
  label: string;
  /** One line on what it does for the customer. */
  blurb: string;
  /** Lowest tier that includes it. */
  tier: PlanTier;
  /** Portal routes the feature owns (a locked account sees the upgrade card there). */
  paths: string[];
  /** What it actually does, in 2 to 4 plain lines (plan pickers, the comparison table). */
  details: string[];
}

export const PLAN_FEATURE_INFO: Record<PlanFeature, PlanFeatureInfo> = {
  followers: {
    key: "followers",
    label: "Follower Tracker",
    blurb: "Daily scans show who followed and who unfollowed, with a daily digest email.",
    tier: "starter",
    paths: ["/followers"],
    details: ["Scans your followers every day: who followed you and who unfollowed.","Gained and lost per day, with a growth chart since your first scan.","Lists of fans and of people who don't follow you back; send anyone to your CRM.","Optional daily digest email."],
  },
  scheduler: {
    key: "scheduler",
    label: "Scheduler",
    blurb: "Plan posts on a calendar for publishing through your connected Chrome extension.",
    tier: "starter",
    paths: ["/scheduler"],
    details: ["A month and week calendar for text, photo, multi-image and video posts.","Publishing runs while Chrome, LinkedIn and the connected extension are available; beta compatibility checks are ongoing.","Anything LinkedIn won't take automatically becomes \"Post by hand\" with the caption ready to paste.","Saved hashtag sets, AI caption ideas and an AI image studio (on your own AI account)."],
  },
  autocaption: {
    key: "autocaption",
    label: "Auto-Caption",
    blurb: "AI looks at each post and writes the caption in your voice, hashtags included.",
    tier: "starter",
    paths: ["/autocaption"],
    details: ["AI uses available post content or media to prepare a caption in your brand voice.","Adds your saved hashtags, AI-picked ones, or both.","Post edits depend on the edit controls and edit window LinkedIn makes available to your account."],
  },
  outreach: {
    key: "outreach",
    label: "Campaigns",
    blurb: "Follow, engage and DM the right people automatically, then follow up and clean up.",
    tier: "growth",
    paths: ["/outreach", "/engage"],
    details: ["Pick an audience: a competitor's followers or engagers, a hashtag, a post's likers or commenters, or your own list.","Filter available results, then queue likes, follows and replies with your pacing settings.","DM them after they follow back or after a delay, with follow-ups and AI-personalized messages.","Welcome new followers, and clean up by unfollowing people who never followed back."],
  },
  autoresponder: {
    key: "autoresponder",
    label: "Autoresponder",
    blurb: "Prepare replies to available LinkedIn Chat conversations using your rules and voice, with approval controls.",
    tier: "growth",
    paths: ["/autoresponder"],
    details: ["Uses your rules or AI to prepare replies to supported conversations in the accessible LinkedIn Chat inbox.","Runs during your chosen active hours while Chrome and the connected extension are available.","Can draft replies for you to approve before anything is sent.","Message requests and encrypted history depend on what LinkedIn Chat exposes; complete inbox coverage is not verified."],
  },
  comments: {
    key: "comments",
    label: "Comment replies",
    blurb: "AI replies to comments on your posts and moves the hot leads into the DMs.",
    tier: "growth",
    paths: ["/comments"],
    details: ["Reads available replies on your recent posts and prepares responses in your voice.","Scores comments as leads and moves the hot ones into your DMs.","Likes the love. Review each reply first, or let it run on its own."],
  },
  autotag: {
    key: "autotag",
    label: "Auto-Tag",
    blurb: "Tell the AI how you tag people and it tags every contact for you.",
    tier: "growth",
    paths: ["/autotag"],
    details: ["Write tagging rules in plain English and pick the tags the AI may use.","The AI reads each contact's profile (and recent posts if you want) and applies the right tags, with the reason.","Templates to start from: business vs personal, coach, city and more."],
  },
  snapshots: {
    key: "snapshots",
    label: "Profile Snapshots",
    blurb: "Capture an accessible profile and latest posts. AI reads it and writes your DM openers.",
    tier: "growth",
    paths: ["/snapshots"],
    details: ["Capture an accessible profile and latest posts, from the LinkedIn sidebar or a campaign.","AI reads it: who they are and what's worth bringing up.","Writes DM openers you can paste. Campaigns can look at the profile before they DM."],
  },
  spotlight: {
    key: "spotlight",
    label: "Spotlight",
    blurb: "Watch accounts in your niche and prepare engagement with their available recent posts.",
    tier: "pro",
    paths: ["/spotlight"],
    details: ["Pick the big accounts in your niche to watch.","Queue a like and an AI-assisted reply when a new post is detected, subject to your pacing settings and LinkedIn availability.","As many automations per LinkedIn account as you want, on your schedule."],
  },
  dream100: {
    key: "dream100",
    label: "Dream 100",
    blurb: "Finds the 100 accounts that already own your audience, with a playbook for each one.",
    tier: "pro",
    paths: ["/dream100", "/research"],
    details: ["Tell it how you want to grow. Magically Linked researches LinkedIn for the accounts your audience already follows.","Scores them and sorts them into Icons, Leaders, Rising and Peers, each with a playbook.","One click to Spotlight a tier, or to run a campaign that borrows their audience."],
  },
  integrations: {
    key: "integrations",
    label: "API & MCP",
    blurb: "API keys and an MCP server, so your other tools and AI assistants can run Magically Linked.",
    tier: "pro",
    paths: [],
    details: ["API keys for your own tools and automations.","An MCP server, so AI assistants like Claude can run Magically Linked for you.","Tell the extension to act from your tools: status, logs and LinkedIn actions."],
  },
};

/** In every plan, can't be switched off: what it is and what it does. */
export const CORE_FEATURES: readonly { label: string; blurb: string }[] = [
  { label: "CRM", blurb: "Every person you talk to, with pipeline stages, tags, notes and next moves (follow-up tasks)." },
  { label: "The Magically Linked sidebar inside LinkedIn", blurb: "See someone's CRM card, notes and saved replies right on www.linkedin.com." },
  { label: "Inbox sync", blurb: "Sync available supported LinkedIn Chat messages into your CRM; request and encrypted-history coverage remains under verification." },
  { label: "Saved replies", blurb: "One-click answers for the messages you send most." },
  { label: "Analytics", blurb: "Available public follower and engagement counts, plus recorded Magically Linked activity. Private reach and impressions are unavailable." },
  { label: "Engagement Buddies", blurb: "Trade likes and comments with other Magically Linked members." },
  { label: "Account safety", blurb: "Daily limits, human pacing, active hours and an automatic pause if LinkedIn pushes back." },
  { label: "Course, help center and support", blurb: "Learn the system and get help when you need it." },
];

/** In every plan, can't be switched off (short labels). */
export const CORE_INCLUDED: readonly string[] = [
  "CRM: pipeline, contacts and next moves",
  "The Magically Linked sidebar inside LinkedIn",
  "Inbox sync for available supported LinkedIn Chat messages",
  "Saved replies",
  "Analytics and the time-saved report",
  "Engagement Buddies",
  "Account-safety limits, active hours and block protection",
  "The Magically Linked course, help center and support",
];

export interface PlanTierInfo {
  id: PlanTier;
  name: string;
  /** US dollars per month. */
  priceMonthly: number;
  tagline: string;
  /** Who it's for, one line. */
  forWho: string;
  features: PlanFeature[];
  /** Default LinkedIn accounts (an admin can raise one account's limit). */
  igAccounts: number;
  limits: PlanLimits;
}

const tierRank: Record<PlanTier, number> = { starter: 0, growth: 1, pro: 2 };
const featuresUpTo = (tier: PlanTier) => PLAN_FEATURES.filter((f) => tierRank[PLAN_FEATURE_INFO[f].tier] <= tierRank[tier]);

export const PLAN_TIER_INFO: Record<PlanTier, PlanTierInfo> = {
  starter: {
    id: "starter",
    name: "Starter",
    priceMonthly: 9,
    tagline: "Know your audience",
    forWho: "Creators and small businesses getting organized on LinkedIn.",
    features: featuresUpTo("starter"),
    igAccounts: 1,
    limits: { liveCampaigns: 0 },
  },
  growth: {
    id: "growth",
    name: "Growth",
    priceMonthly: 49,
    tagline: "Your funnel on autopilot",
    forWho: "Businesses that want new followers and DMs turned into leads every day.",
    features: featuresUpTo("growth"),
    igAccounts: 2,
    limits: { liveCampaigns: 3 },
  },
  pro: {
    id: "pro",
    name: "Pro",
    priceMonthly: 99,
    tagline: "Borrow big audiences",
    forWho: "Serious brands, coaches and operators running several accounts.",
    features: featuresUpTo("pro"),
    igAccounts: 5,
    limits: { liveCampaigns: null },
  },
};

/** The standard tier an account's stored plan means. Legacy plans that had everything stay Pro. */
export function planTierFor(plan: Plan | string): PlanTier {
  return plan === "starter" ? "starter" : plan === "growth" ? "growth" : "pro";
}

export function isPlanFeature(v: unknown): v is PlanFeature {
  return typeof v === "string" && (PLAN_FEATURES as readonly string[]).includes(v);
}

/** Feature switches on top of a tier (a reseller package, or one account). */
export interface FeatureSwitches {
  on: PlanFeature[];
  off: PlanFeature[];
}

export const NO_SWITCHES: FeatureSwitches = { on: [], off: [] };

/** Clean stored/untrusted switches: known features only, no duplicates, a feature in both lists is off. */
export function normalizeSwitches(raw: unknown): FeatureSwitches {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const list = (v: unknown) => (Array.isArray(v) ? [...new Set(v.filter(isPlanFeature))] : []);
  const off = list(o.off);
  return { on: list(o.on).filter((f) => !off.includes(f)), off };
}

/** Switches that turn `base` into exactly `desired` (what the portal's checkboxes save). */
export function switchesBetween(base: readonly PlanFeature[], desired: readonly PlanFeature[]): FeatureSwitches {
  return {
    on: PLAN_FEATURES.filter((f) => desired.includes(f) && !base.includes(f)),
    off: PLAN_FEATURES.filter((f) => !desired.includes(f) && base.includes(f)),
  };
}

/** Switches that turn a plain tier into exactly `features`. */
export function switchesFor(tier: PlanTier, features: readonly PlanFeature[]): FeatureSwitches {
  return switchesBetween(PLAN_TIER_INFO[tier].features, features);
}

export function isEmptySwitches(s: FeatureSwitches | null | undefined): boolean {
  return !s || (s.on.length === 0 && s.off.length === 0);
}

/** Features a package gives (its tier plus its switches). */
export function packageFeatures(pkg: Pick<ResellerPackage, "basePlan" | "switches">): PlanFeature[] {
  return applySwitches(PLAN_TIER_INFO[pkg.basePlan].features, pkg.switches);
}

export function isPlanTier(v: unknown): v is PlanTier {
  return typeof v === "string" && (PLAN_TIERS as readonly string[]).includes(v);
}

export function applySwitches(features: readonly PlanFeature[], s: FeatureSwitches | null | undefined): PlanFeature[] {
  if (!s) return [...features];
  const set = new Set(features);
  for (const f of s.on) set.add(f);
  for (const f of s.off) set.delete(f);
  return PLAN_FEATURES.filter((f) => set.has(f));
}

/** A reseller's own named plan: a tier plus feature switches, optionally with what they charge. */
export interface ResellerPackage {
  id: string;
  name: string;
  description: string;
  /** What the reseller charges for it, free text ("$297/mo"). Shown to the reseller only. */
  priceLabel: string;
  basePlan: PlanTier;
  switches: FeatureSwitches;
  /** Accounts on this package (list responses only). */
  accountCount?: number;
  createdAt: number;
  updatedAt: number;
}

export interface Entitlements {
  /** The standard tier the account is measured against. */
  tier: PlanTier;
  /** Effective features, in catalog order. */
  features: PlanFeature[];
  limits: PlanLimits;
  /** Set when the account is on one of its reseller's packages. */
  packageId: string | null;
  packageName: string | null;
  /** True when the features differ from the plain tier (package or per-account switches). */
  custom: boolean;
  /** Admins and resellers: everything, always. */
  operator: boolean;
}

export interface EntitlementInput {
  role: Role;
  plan: Plan | string;
  package?: Pick<ResellerPackage, "id" | "name" | "basePlan" | "switches"> | null;
  switches?: FeatureSwitches | null;
}

export function resolveEntitlements(input: EntitlementInput): Entitlements {
  if (input.role === "admin" || input.role === "reseller") {
    return { tier: "pro", features: [...PLAN_FEATURES], limits: { ...PLAN_TIER_INFO.pro.limits }, packageId: null, packageName: null, custom: false, operator: true };
  }
  const pkg = input.package ?? null;
  const tier = pkg ? pkg.basePlan : planTierFor(input.plan);
  const base = PLAN_TIER_INFO[tier];
  const features = applySwitches(applySwitches(base.features, pkg?.switches), input.switches);
  return {
    tier,
    features,
    limits: limitsFor(tier, features),
    packageId: pkg?.id ?? null,
    packageName: pkg?.name ?? null,
    custom: features.length !== base.features.length || features.some((f) => !base.features.includes(f)),
    operator: false,
  };
}

/** Tier limits, with features switched on outside the tier taking the lowest tier that has them. */
export function limitsFor(tier: PlanTier, features: readonly PlanFeature[]): PlanLimits {
  const limits = { ...PLAN_TIER_INFO[tier].limits };
  if (!features.includes("outreach")) limits.liveCampaigns = 0;
  else if (limits.liveCampaigns === 0) limits.liveCampaigns = PLAN_TIER_INFO[PLAN_FEATURE_INFO.outreach.tier].limits.liveCampaigns;
  return limits;
}

export function hasFeature(e: Pick<Entitlements, "features"> | null | undefined, f: PlanFeature): boolean {
  return !!e && e.features.includes(f);
}

/** Cheapest standard tier that includes `f` (for "Campaigns is on Growth and Pro"). */
export function lowestTierWith(f: PlanFeature): PlanTier {
  return PLAN_FEATURE_INFO[f].tier;
}

/** The plan feature a portal path belongs to, if any ("/outreach/abc" → "outreach"). */
export function planFeatureForPath(pathname: string): PlanFeature | null {
  for (const f of PLAN_FEATURES) {
    for (const p of PLAN_FEATURE_INFO[f].paths) if (pathname === p || pathname.startsWith(p + "/")) return f;
  }
  return null;
}

// ── API gate ────────────────────────────────────────────────────────────────
// Which /api prefix belongs to which plan feature. Paths are relative to /api.
// `open` sub-paths stay reachable on every plan because other features depend on them.

export interface ApiGate {
  prefix: string;
  feature: PlanFeature;
  open?: string[];
}

export const PLAN_API_GATES: readonly ApiGate[] = [
  // The Home + Analytics pages show the tracker's summary when there is one.
  { prefix: "/followers", feature: "followers", open: ["/followers/summary"] },
  { prefix: "/scheduler", feature: "scheduler" },
  { prefix: "/autocaption", feature: "autocaption" },
  { prefix: "/outreach", feature: "outreach" },
  { prefix: "/engage", feature: "outreach" },
  { prefix: "/connections", feature: "outreach" },
  // Inbox sync feeds the CRM and every feature's reply detection; it is not the Autoresponder.
  { prefix: "/autoresponder", feature: "autoresponder", open: ["/autoresponder/sync"] },
  { prefix: "/comments", feature: "comments" },
  { prefix: "/autotag", feature: "autotag" },
  { prefix: "/snapshots", feature: "snapshots" },
  { prefix: "/spotlight", feature: "spotlight" },
  { prefix: "/dream100", feature: "dream100" },
  { prefix: "/integrations", feature: "integrations" },
  { prefix: "/mcp", feature: "integrations" },
];

/** The plan feature an /api path needs (`path` without the /api prefix), or null when it's open. */
export function planFeatureForApiPath(path: string): PlanFeature | null {
  for (const g of PLAN_API_GATES) {
    if (path !== g.prefix && !path.startsWith(g.prefix + "/")) continue;
    if (g.open?.some((o) => path === o || path.startsWith(o + "/"))) return null;
    return g.feature;
  }
  return null;
}

// ── Extension jobs ──────────────────────────────────────────────────────────
// Engine jobs that belong to a plan feature (a job's own `feature` tag is about activity logging,
// so it can't be used: inbox sync is tagged autoresponder, buddies are tagged engage). Jobs not
// listed here run on every plan.

export const PLAN_JOB_FEATURE: Readonly<Record<string, PlanFeature>> = {
  "followers.scan": "followers",
  "scheduler.publish": "scheduler",
  "autocaption.run": "autocaption",
  "autocaption.preview": "autocaption",
  "engage.connection-requests": "outreach",
  "outreach.send": "outreach",
  "outreach.followback": "outreach",
  "engage.run": "outreach",
  "engage.followback": "outreach",
  "autoresponder.send": "autoresponder",
  "comments.sync": "comments",
  "comments.act": "comments",
  "autotag.run": "autotag",
  "snapshots.capture": "snapshots",
  "spotlight.run": "spotlight",
  "dream100.discover": "dream100",
};

/** May the engine run this job? Unknown entitlements (an older API) allow everything. */
export function jobEntitled(jobId: string, e: Pick<Entitlements, "features"> | null | undefined): boolean {
  const f = PLAN_JOB_FEATURE[jobId];
  return !f || !e || e.features.includes(f);
}

/** The 403 an API request gets for a feature the account's plan doesn't include. */
export const FEATURE_LOCKED = "feature_locked";

export function featureLockedMessage(f: PlanFeature): string {
  const info = PLAN_FEATURE_INFO[f];
  const tiers = PLAN_TIERS.filter((t) => PLAN_TIER_INFO[t].features.includes(f)).map((t) => PLAN_TIER_INFO[t].name);
  return `${info.label} isn't on your plan. It's included in ${tiers.join(" and ")}.`;
}

// ── Trials + billing ────────────────────────────────────────────────────────

/** Starter, Growth and Pro start with a free trial. A card is required (docs/TRIALS.md). */
export const TRIAL_DAYS = 14;

/** Stripe price lookup keys for each tier's monthly price (app=magicallylinked, price metadata gf_plan). */
export const PLAN_PRICE_LOOKUP: Readonly<Record<PlanTier, string>> = {
  starter: "ml_starter_monthly_9",
  growth: "ml_growth_monthly_49",
  pro: "ml_pro_monthly_99",
};

/** The Reseller Program's regular price: $100/month for up to 300 managed accounts, no trial. */
export const RESELLER_PRICE_MONTHLY = 100;
